import React from "react";
import "./retro.css";

const cx = (...names) => names.filter(Boolean).join(" ");

/* ---------- Layout ---------- */

export function ScreenFrame({ children, className = "" }) {
  return (
    <main className={cx("rt-screen", className)}>
      <div className="rt-screen-inner">{children}</div>
    </main>
  );
}

export function PixelFrame({ children, className = "" }) {
  return <div className={cx("rt-pixel-frame", className)}>{children}</div>;
}

export function PixelPanel({ children, className = "" }) {
  return <section className={cx("rt-panel", className)}>{children}</section>;
}

/* ---------- Controls ---------- */

/**
 * variant: "primary" (green, main action) | "paper" (default) | "blue" | "danger" | "steel" | "ghost"
 * size: "sm" | "md" | "lg"
 */
export function PixelButton({
  children,
  className = "",
  variant = "paper",
  size = "md",
  block = false,
  as: Component = "button",
  type,
  ...props
}) {
  return (
    <Component
      type={Component === "button" ? type || "button" : type}
      className={cx("rt-button", `rt-button-${variant}`, `rt-button-${size}`, block && "rt-button-block", className)}
      {...props}
    >
      {children}
    </Component>
  );
}

export const PixelInput = React.forwardRef(function PixelInput({ className = "", ...props }, ref) {
  return <input ref={ref} className={cx("rt-input", className)} {...props} />;
});

export function PasswordInput({ showLabel = "Show password", ...props }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <div className="rt-password-field">
      <PixelInput {...props} type={visible ? "text" : "password"} />
      <button
        type="button"
        className="rt-password-toggle"
        aria-label={showLabel}
        aria-pressed={visible}
        onClick={() => setVisible((v) => !v)}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          {visible ? (
            <>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
              <circle cx="12" cy="12" r="3" />
            </>
          ) : (
            <>
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
}

export function PixelField({ label, help, children }) {
  return (
    <label className="rt-field">
      {label && <span className="rt-field-label">{label}</span>}
      {children}
      {help && <span className="rt-field-help">{help}</span>}
    </label>
  );
}

export function PixelToggle({ checked, onChange, className = "", disabled = false, label }) {
  return (
    <button
      type="button"
      className={cx("rt-toggle", checked && "rt-toggle-on", className)}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
    >
      <span className="rt-toggle-knob" />
    </button>
  );
}

/* ---------- Modal ---------- */

export function PixelModal({
  open,
  onClose,
  title,
  children,
  className = "",
  size = "md",
  closeLabel = "Close",
}) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="rt-modal-overlay"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}
    >
      <section
        className={cx("rt-modal", `rt-modal-${size}`, !title && "rt-modal-noheader", className)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : closeLabel}
      >
        <header className="rt-modal-head">
          {title ? <h2 className="rt-modal-title">{title}</h2> : <span />}
          <button type="button" className="rt-modal-close" onClick={onClose} aria-label={closeLabel}>
            ×
          </button>
        </header>
        <div className="rt-modal-body">{children}</div>
      </section>
    </div>
  );
}

export function PixelModalActions({ children, className = "" }) {
  return <div className={cx("rt-modal-actions", className)}>{children}</div>;
}

export function PixelConfirm({
  open,
  title,
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  confirmVariant = "danger",
  onConfirm,
  onCancel,
}) {
  return (
    <PixelModal open={open} onClose={onCancel} size="sm" title={title} closeLabel={cancelText}>
      {message && <p className="rt-confirm-text">{message}</p>}
      <PixelModalActions>
        <PixelButton variant="paper" onClick={onCancel}>
          {cancelText}
        </PixelButton>
        <PixelButton variant={confirmVariant} onClick={onConfirm}>
          {confirmText}
        </PixelButton>
      </PixelModalActions>
    </PixelModal>
  );
}

/* ---------- Text ---------- */

export function PixelHeading({ children, className = "", level = 1 }) {
  const Tag = `h${level}`;
  return <Tag className={cx("rt-heading", className)}>{children}</Tag>;
}

export function PixelText({ children, className = "", muted = false, as: Component = "p" }) {
  return <Component className={cx("rt-text", muted && "rt-text-muted", className)}>{children}</Component>;
}

export function PixelDivider({ label = "OR", className = "" }) {
  return (
    <div className={cx("rt-divider", className)}>
      <span>{label}</span>
    </div>
  );
}

export function FooterCredit({ children }) {
  return <footer className="rt-footer-credit">{children}</footer>;
}

export function LanguageButton({ locale, onToggle }) {
  return (
    <PixelButton
      className="rt-lang-button"
      variant="steel"
      size="sm"
      onClick={onToggle}
      aria-label={locale === "vi_VN" ? "Switch to English" : "Chuyển sang tiếng Việt"}
    >
      {locale === "vi_VN" ? "VI" : "EN"}
    </PixelButton>
  );
}

/* ---------- Auth ---------- */

export function AuthScreen({ locale, onToggleLanguage, heading, subtitle, children, footer }) {
  return (
    <ScreenFrame className="rt-auth-screen">
      <div className="rt-topbar">
        <p className="rt-brand">BIP SCORE</p>
        <LanguageButton locale={locale} onToggle={onToggleLanguage} />
      </div>
      <section className="rt-auth-content">
        <div className="rt-hero">
          <PixelHeading className="rt-hero-title">{heading}</PixelHeading>
          <PixelText className="rt-hero-subtitle">{subtitle}</PixelText>
        </div>
        {children}
      </section>
      {footer && <FooterCredit>{footer}</FooterCredit>}
    </ScreenFrame>
  );
}

export function LoginPanel({ title, subtitle, children, prompt }) {
  return (
    <section className="rt-login-panel">
      <div className="rt-login-head">
        <PixelHeading level={2} className="rt-login-title">{title}</PixelHeading>
        <PixelText className="rt-login-subtitle" muted>{subtitle}</PixelText>
      </div>
      <div className="rt-form-stack">{children}</div>
      {prompt}
    </section>
  );
}

export function GoogleLoginButton({ children }) {
  return <div className="rt-google-login">{children}</div>;
}

export function SignupPrompt({ text, actionText, onAction }) {
  return (
    <p className="rt-text rt-signup-prompt">
      <span className="rt-text-muted">{text}</span>{" "}
      <button type="button" className="rt-link-button" onClick={onAction}>
        {actionText}
      </button>
    </p>
  );
}

/* ---------- Home ---------- */

export function MenuActionCard({ title, iconSrc, disabled = false, onClick, className = "" }) {
  return (
    <button
      type="button"
      className={cx("rt-menu-card", disabled && "rt-disabled", className)}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {iconSrc && <img className="rt-menu-card-icon" src={iconSrc} alt="" aria-hidden="true" />}
      <span className="rt-menu-card-title">{title}</span>
    </button>
  );
}

export function MenuActionGrid({ children }) {
  return <div className="rt-menu-grid">{children}</div>;
}

/* ---------- Scoreboard ---------- */

export function ScorePill({ value, className = "" }) {
  const state = Number(value) > 0 ? "positive" : "negative";
  return <div className={cx("rt-score-pill", `rt-score-pill-${state}`, className)}>{value}</div>;
}

export function CircleActionButton({ children, className = "", variant = "paper", ...props }) {
  return (
    <button type="button" className={cx("rt-circle-button", `rt-circle-button-${variant}`, className)} {...props}>
      {children}
    </button>
  );
}

export function PlayerScoreRow({
  name,
  score,
  isCurrentUser,
  youLabel = "You",
  onPay,
  onLog,
  payIcon,
  logIcon,
  payLabel,
  logLabel,
  extraActions,
}) {
  return (
    <div className={cx("rt-player-row", isCurrentUser && "rt-player-row-me")}>
      <div className="rt-player-info">
        <div className="rt-player-name">{name}</div>
        {isCurrentUser && <span className="rt-player-tag">{youLabel}</span>}
      </div>
      <ScorePill value={score} />
      <div className="rt-player-actions">
        {extraActions}
        {onPay !== undefined &&
          (isCurrentUser ? (
            <span className="rt-circle-placeholder" />
          ) : (
            <CircleActionButton variant="gold" onClick={onPay} aria-label={payLabel || `Transfer to ${name}`}>
              {payIcon}
            </CircleActionButton>
          ))}
        {onLog && (
          <CircleActionButton onClick={onLog} aria-label={logLabel || `History of ${name}`}>
            {logIcon}
          </CircleActionButton>
        )}
      </div>
    </div>
  );
}

export function ConnectionStatus({ connected, liveLabel, offlineLabel }) {
  return (
    <span className={cx("rt-board-status", connected && "rt-status-live")} role="status">
      <span className="rt-status-dot" aria-hidden="true" />
      {connected ? liveLabel : offlineLabel}
    </span>
  );
}

export function BoardHeader({ left, title, status, right }) {
  return (
    <header className="rt-board-header">
      {left}
      <div className="rt-board-title">
        <h1 className="rt-board-name">{title}</h1>
        {status}
      </div>
      {right}
    </header>
  );
}

export function BottomBar({ children }) {
  return <div className="rt-bottom-bar">{children}</div>;
}
